import React, { useState } from 'react';
import { apiClient } from '../../../api/client';

type BlockMode = 'full' | 'partial' | 'soft' | 'hard';

interface BlockDialogProps {
  tableId: string;
  tableNumber: number;
  capacity: number;
  onClose: () => void;
  onSuccess: () => void;
}

const BLOCK_CATEGORIES = ['VIP', 'Chapter', 'Sponsor', 'Family'];

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '480px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: 0,
  },
  closeBtn: {
    padding: '0.3rem 0.6rem',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#666',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    marginBottom: '1rem',
    borderBottom: '1px solid #e0e0e0',
  },
  tab: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
  },
  tabActive: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#1a6b3c',
    borderBottom: '2px solid #1a6b3c',
    marginBottom: '-1px',
  },
  formGroup: {
    marginBottom: '0.85rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: '0.3rem',
  },
  input: {
    width: '100%',
    padding: '0.45rem 0.65rem',
    fontSize: '0.85rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.45rem 0.65rem',
    fontSize: '0.85rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  textarea: {
    width: '100%',
    padding: '0.45rem 0.65rem',
    fontSize: '0.85rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    minHeight: '60px',
    resize: 'vertical' as const,
  },
  submitBtn: {
    padding: '0.55rem 1.2rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  hint: {
    fontSize: '0.72rem',
    color: '#888',
    marginTop: '0.2rem',
  },
};

export function BlockDialog({ tableId, tableNumber, capacity, onClose, onSuccess }: BlockDialogProps) {
  const [mode, setMode] = useState<BlockMode>('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Full block form state
  const [fullCategory, setFullCategory] = useState('VIP');
  const [fullGroup, setFullGroup] = useState('');

  // Partial block form state
  const [partialCount, setPartialCount] = useState(1);
  const [partialGroup, setPartialGroup] = useState('');
  const [partialPositions, setPartialPositions] = useState('');

  // Soft block form state
  const [softSeatCount, setSoftSeatCount] = useState(1);
  const [softExpiration, setSoftExpiration] = useState('');
  const [softReason, setSoftReason] = useState('');

  // Hard block form state
  const [hardSeatCount, setHardSeatCount] = useState(1);
  const [hardReason, setHardReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      switch (mode) {
        case 'full':
          await apiClient(`/table-management/tables/${tableId}/full-block`, {
            method: 'POST',
            body: {
              blockCategory: fullCategory,
              reservedGroup: fullGroup,
            },
          });
          break;

        case 'partial': {
          const body: Record<string, unknown> = {
            seatCount: partialCount,
            reservedGroup: partialGroup,
          };
          if (partialPositions.trim()) {
            body.seatPositions = partialPositions
              .split(',')
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !isNaN(n));
          }
          await apiClient(`/table-management/tables/${tableId}/partial-block`, {
            method: 'POST',
            body,
          });
          break;
        }

        case 'soft':
          await apiClient('/table-management/seats/soft-block', {
            method: 'POST',
            body: {
              tableId,
              seatCount: softSeatCount,
              expiration: softExpiration,
              holdReason: softReason,
            },
          });
          break;

        case 'hard':
          await apiClient('/table-management/seats/hard-block', {
            method: 'POST',
            body: {
              tableId,
              seatCount: hardSeatCount,
              blockReason: hardReason,
            },
          });
          break;
      }

      onSuccess();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; error?: string };
      setError(apiErr?.message || apiErr?.error || 'Operation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close on Escape
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Block dialog for Table ${tableNumber}`}
    >
      <div style={styles.dialog}>
        <div style={styles.header}>
          <h3 style={styles.title}>Block — Table {tableNumber}</h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close dialog">
            ✕
          </button>
        </div>

        <div style={styles.tabs} role="tablist" aria-label="Block type">
          {([
            { key: 'full', label: 'Full Block' },
            { key: 'partial', label: 'Partial Block' },
            { key: 'soft', label: 'Soft Block' },
            { key: 'hard', label: 'Hard Block' },
          ] as { key: BlockMode; label: string }[]).map((tab) => (
            <button
              key={tab.key}
              style={mode === tab.key ? styles.tabActive : styles.tab}
              onClick={() => setMode(tab.key)}
              role="tab"
              aria-selected={mode === tab.key}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && <div style={styles.error} role="alert">{error}</div>}

        <form onSubmit={handleSubmit}>
          {mode === 'full' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="full-category">Block Category</label>
                <select
                  id="full-category"
                  style={styles.select}
                  value={fullCategory}
                  onChange={(e) => setFullCategory(e.target.value)}
                >
                  {BLOCK_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="full-group">Reserved Group Name</label>
                <input
                  id="full-group"
                  style={styles.input}
                  type="text"
                  value={fullGroup}
                  onChange={(e) => setFullGroup(e.target.value)}
                  placeholder="e.g., Johnson Family"
                  maxLength={100}
                  required
                />
              </div>
            </>
          )}

          {mode === 'partial' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="partial-count">Number of Seats</label>
                <input
                  id="partial-count"
                  style={styles.input}
                  type="number"
                  min={1}
                  max={capacity - 1}
                  value={partialCount}
                  onChange={(e) => setPartialCount(parseInt(e.target.value, 10) || 1)}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="partial-group">Reserved Group Name</label>
                <input
                  id="partial-group"
                  style={styles.input}
                  type="text"
                  value={partialGroup}
                  onChange={(e) => setPartialGroup(e.target.value)}
                  placeholder="e.g., Smith Chapter"
                  maxLength={100}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="partial-positions">Specific Seat Positions (optional)</label>
                <input
                  id="partial-positions"
                  style={styles.input}
                  type="text"
                  value={partialPositions}
                  onChange={(e) => setPartialPositions(e.target.value)}
                  placeholder="e.g., 1, 2, 5"
                />
                <div style={styles.hint}>Comma-separated seat numbers (1–{capacity}). Leave blank for automatic.</div>
              </div>
            </>
          )}

          {mode === 'soft' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="soft-count">Number of Seats</label>
                <input
                  id="soft-count"
                  style={styles.input}
                  type="number"
                  min={1}
                  max={capacity}
                  value={softSeatCount}
                  onChange={(e) => setSoftSeatCount(parseInt(e.target.value, 10) || 1)}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="soft-expiration">Expiration Date & Time</label>
                <input
                  id="soft-expiration"
                  style={styles.input}
                  type="datetime-local"
                  value={softExpiration}
                  onChange={(e) => setSoftExpiration(e.target.value)}
                  required
                />
                <div style={styles.hint}>Must be at least 1 hour from now and within 90 days.</div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="soft-reason">Hold Reason</label>
                <textarea
                  id="soft-reason"
                  style={styles.textarea}
                  value={softReason}
                  onChange={(e) => setSoftReason(e.target.value)}
                  placeholder="Reason for the temporary hold..."
                  maxLength={200}
                  required
                />
              </div>
            </>
          )}

          {mode === 'hard' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="hard-count">Number of Seats</label>
                <input
                  id="hard-count"
                  style={styles.input}
                  type="number"
                  min={1}
                  max={capacity}
                  value={hardSeatCount}
                  onChange={(e) => setHardSeatCount(parseInt(e.target.value, 10) || 1)}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="hard-reason">Block Reason</label>
                <textarea
                  id="hard-reason"
                  style={styles.textarea}
                  value={hardReason}
                  onChange={(e) => setHardReason(e.target.value)}
                  placeholder="Reason for the protected block (Admin only)..."
                  maxLength={200}
                  required
                />
                <div style={styles.hint}>Hard blocks can only be modified by Admin users.</div>
              </div>
            </>
          )}

          <button
            type="submit"
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Apply Block'}
          </button>
        </form>
      </div>
    </div>
  );
}
